import React, { useEffect, useState } from "react"
import {
  useGenerateExtensionTokenMutation,
  useLazyGetExtensionTokenQuery,
  useRevokeExtensionTokenMutation,
} from "../../../../../app/services/book/saved_tours"

const formatDate = (iso) => {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return null
  }
}

const ExtensionConnectPanel = ({ open, onClose }) => {
  const [fetchStatus, { data: statusData, isFetching: statusLoading }] =
    useLazyGetExtensionTokenQuery()
  const [generateToken, { isLoading: generating }] = useGenerateExtensionTokenMutation()
  const [revokeToken, { isLoading: revoking }] = useRevokeExtensionTokenMutation()

  const [connectionString, setConnectionString] = useState(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)
  const [confirmRevoke, setConfirmRevoke] = useState(false)

  useEffect(() => {
    if (open) {
      fetchStatus()
      setConnectionString(null)
      setCopied(false)
      setError(null)
      setConfirmRevoke(false)
    }
  }, [open, fetchStatus])

  if (!open) return null

  const connected = !!statusData?.connected

  const handleGenerate = async () => {
    setError(null)
    try {
      const res = await generateToken().unwrap()
      setConnectionString(res.connection_string)
      setCopied(false)
      fetchStatus()
    } catch (e) {
      setError("Не удалось сгенерировать код. Попробуйте ещё раз.")
    }
  }

  const handleRevoke = async () => {
    setError(null)
    try {
      await revokeToken().unwrap()
      setConnectionString(null)
      setConfirmRevoke(false)
      fetchStatus()
    } catch (e) {
      setError("Не удалось отозвать доступ. Попробуйте ещё раз.")
    }
  }

  const handleCopy = async () => {
    if (!connectionString) return
    try {
      await navigator.clipboard.writeText(connectionString)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError("Не удалось скопировать — выделите код вручную.")
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      <div
        className="relative w-full md:max-w-md bg-white rounded-t-2xl md:rounded-2xl border border-[#E7EAEF]
                   shadow-xl p-5 md:p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base md:text-lg font-semibold text-[#1F2937]">Tour Importer</h2>
          <button
            onClick={onClose}
            className="text-[#9AA5B4] hover:text-[#1F2937] transition-colors text-xl leading-none px-1"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        {statusLoading && !statusData && (
          <div className="text-sm text-[#9AA5B4] py-6 text-center">Проверяем статус…</div>
        )}

        {statusData && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <span
                className={`w-2 h-2 rounded-full ${connected ? "bg-[#22C55E]" : "bg-[#D1D5DB]"}`}
              />
              <span className="text-sm font-medium text-[#1F2937]">
                {connected ? "Подключено" : "Не подключено"}
              </span>
            </div>

            {connected && !connectionString && (
              <div className="text-xs text-[#9AA5B4] mb-4 space-y-0.5">
                {statusData.generated_at && (
                  <div>Создано: {formatDate(statusData.generated_at)}</div>
                )}
                <div>
                  Использовано:{" "}
                  {statusData.last_used_at ? formatDate(statusData.last_used_at) : "ещё ни разу"}
                </div>
              </div>
            )}

            {!connected && !connectionString && (
              <p className="text-sm text-[#6B7280] mb-4">
                Сгенерируйте код и вставьте его в поле настроек расширения Tour Importer, чтобы
                сохранённые туры попадали прямо в этот аккаунт.
              </p>
            )}

            {connectionString && (
              <div className="mb-4">
                <div className="text-xs text-[#6B7280] mb-2">
                  Скопируйте код целиком — он показывается только один раз:
                </div>
                <div className="bg-[#F4F6F9] border border-[#E7EAEF] rounded-xl p-3 mb-2">
                  <code className="text-xs text-[#1F2937] break-all font-mono">
                    {connectionString}
                  </code>
                </div>
                <button
                  onClick={handleCopy}
                  className="w-full py-2.5 rounded-xl bg-[#1F2937] text-white text-sm font-medium
                             hover:bg-[#111827] transition-colors"
                >
                  {copied ? "Скопировано ✓" : "Скопировать код"}
                </button>
              </div>
            )}

            {error && <div className="text-xs text-[#EF4444] mb-3">{error}</div>}

            <div className="flex flex-col md:flex-row gap-2 mt-2">
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex-1 py-2.5 rounded-xl border border-[#E7EAEF] text-sm font-medium
                           text-[#1F2937] hover:bg-[#F4F6F9] transition-colors disabled:opacity-50"
              >
                {generating ? "Генерируем…" : connected ? "Перевыпустить код" : "Сгенерировать код"}
              </button>

              {connected && (
                <>
                  {!confirmRevoke ? (
                    <button
                      onClick={() => setConfirmRevoke(true)}
                      disabled={revoking}
                      className="flex-1 py-2.5 rounded-xl border border-[#FCA5A5] text-sm font-medium
                                 text-[#EF4444] hover:bg-[#FEF2F2] transition-colors disabled:opacity-50"
                    >
                      Отозвать доступ
                    </button>
                  ) : (
                    <button
                      onClick={handleRevoke}
                      disabled={revoking}
                      className="flex-1 py-2.5 rounded-xl bg-[#EF4444] text-white text-sm font-medium
                                 hover:bg-[#DC2626] transition-colors disabled:opacity-50"
                    >
                      {revoking ? "Отзываем…" : "Точно отозвать?"}
                    </button>
                  )}
                </>
              )}
            </div>

            {connected && (
              <p className="text-[11px] text-[#9AA5B4] mt-3">
                Перевыпуск или отзыв кода немедленно отключает текущее расширение — следующий запрос
                от него завершится ошибкой авторизации.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ExtensionConnectPanel
